import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../config";
import {
  Debt,
  DebtUpdateRequest,
  DebtCreateRequest,
} from "../../../../api/src/types";
import { prepareHeaders } from "../utils";

interface BasePathParams {
  userId: string;
  docId: string;
}

// linter takes away this const

// eslint-disable-next-line
const debtsTagType = "debts" as const;

export const debtsApi = createApi({
  reducerPath: "debtsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: [debtsTagType],
  endpoints: (builder) => ({
    getDebt: builder.query<Debt, BasePathParams & { id: string }>({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/debts/${id}`,
        method: "GET",
      }),

      providesTags: (_, __, query) => [{ type: debtsTagType, id: query.id }],
    }),
    getDebtsByUserId: builder.query<Debt[], BasePathParams>({
      query: ({ userId, docId }) => {
        return `users/${userId}/documents/${docId}/debts`;
      },
      merge: (currentCache, newItems) => {
        const itemMap = new Map(currentCache.map((item) => [item.id, item]));
        newItems.forEach((item) => itemMap.set(item.id, item));
        return Array.from(itemMap.values());
      },

      providesTags: (result) => [
        ...(result?.map((debt) => ({
          type: debtsTagType,
          id: debt.id,
        })) ?? []),
        { type: debtsTagType, id: "LIST" },
      ],
    }),

    updateDebt: builder.mutation<
      Debt,
      BasePathParams & { id: string; data: DebtUpdateRequest }
    >({
      query: ({ userId, docId, id, data }) => ({
        url: `users/${userId}/documents/${docId}/debts/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (_, __, { id }) => [{ type: debtsTagType, id }],
    }),

    createDebt: builder.mutation<
      Debt,
      BasePathParams & { data: DebtCreateRequest }
    >({
      query: ({ userId, docId, data }) => ({
        url: `users/${userId}/documents/${docId}/debts`,
        method: "POST",
        body: data,
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newItem } = await queryFulfilled;
          if (newItem.userId && newItem.id && newItem.documentId) {
            dispatch(
              debtsApi.util.updateQueryData(
                "getDebtsByUserId",
                { userId: newItem.userId, docId: newItem.documentId },
                (draft: Debt[]) => {
                  draft.push(newItem);
                },
              ),
            );
          }
        } catch (error) {
          console.log("rtk query create debt error", error);
        }
      },
    }),
    deleteDebt: builder.mutation<Debt, BasePathParams & { id: string }>({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/debts/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(
        { userId, docId, id },
        { dispatch, queryFulfilled },
      ) {
        await queryFulfilled;
        dispatch(
          debtsApi.util.updateQueryData(
            "getDebtsByUserId",
            { userId, docId },
            (draft: Debt[]) => {
              return draft.filter((item) => item.id !== id);
            },
          ),
        );
      },
    }),
  }),
});

export const {
  useGetDebtQuery,
  useGetDebtsByUserIdQuery,
  useUpdateDebtMutation,
  useCreateDebtMutation,
  useDeleteDebtMutation,
} = debtsApi;
